// @givmo/app-admin — the donation summary at the head of the portal's charge dialogs (CAM-X4 web).
//
// D-CAM-41 (Jonathan, 2026-09-25): the Give and approve dialogs are built around the donation itself
// — who receives it, how much, where the money comes from, and who the donor of record is — with the
// donor disclosure below it, bounded, rather than the other way round. This is that focal point, one
// component so the two dialogs present a gift the same way: the recipient's mark (its logo, through the
// portal's Avatar, which falls back to initials when there is none or it fails to load), the amount as
// the largest thing on the card, "to <recipient>", optional quiet context lines, then one card holding
// an optional compact list (Approve all's matches) and the label/value rows.
//
// Every value arrives formatted by the caller (money is only ever formatted, never recomputed here),
// and every amount uses tabular figures like every other money value in the portal.

import type { ReactNode } from 'react';
import { Avatar, testIdProp } from '@givmo/design-system';
import { LockMark } from './lock-mark';

export interface ChargeSummaryRow {
  label: string;
  value: ReactNode;
  testId?: string;
}

/**
 * A recipient's mark: its logo, or initials from its name — or, `locked`, the neutral lock, for a
 * charity collection the company may not see the picture of (EP-WEB40).
 */
export interface ChargeSummaryMark {
  name: string;
  logoUrl: string | null;
  locked?: boolean;
}

/** One line of the compact list (Approve all): a recipient, a quiet sub-line and its amount. */
export interface ChargeSummaryItem {
  key: string;
  mark: ChargeSummaryMark;
  title: string;
  /** One quiet line under the title ("Matching Ada Lovelace"), or null. */
  subtitle: string | null;
  /** The formatted amount. */
  amount: string;
  testId?: string;
}

export interface ChargeSummaryProps {
  /** The recipient's mark: its logo, or initials from its name. Absent for a batch. */
  mark?: ChargeSummaryMark | null;
  /** The amount, formatted (e.g. "$250"). */
  amount: string;
  /** A quiet qualifier over the amount ("Up to" for a batch whose legs are capped), or null. */
  amountQualifier?: string | null;
  /**
   * Who receives it — rendered as "to <recipient>", or, as a phrase, "to {lead}<name>{trail}" ("to
   * the <Bay Area Food Banks> collection", EP-WEB40). Absent for a batch.
   */
  recipient?: string | { lead: string; name: string; trail: string } | null;
  /** A control under the recipient: a collection's "{N} charities ›" (EP-WEB40). */
  recipientAction?: ReactNode;
  /** What that control opens, at the top of the card, above the rows. */
  cardTop?: ReactNode;
  /** A plain line under the amount when there is no single recipient ("for 4 matches"). */
  caption?: string | null;
  /**
   * Quiet line(s) under the recipient: what an ETF or fundraiser is ("Cause ETF · 6 charities"), whose
   * gift a match pays ("Matching Ada Lovelace’s gift"). Each entry is its own line.
   */
  context?: string | readonly string[] | null;
  /** The compact list (Approve all), shown in the card above the rows. */
  items?: readonly ChargeSummaryItem[];
  /** A last, quiet list line for what the list leaves out ("+ 2 more matches"), or null. */
  itemsOverflow?: string | null;
  rows: readonly ChargeSummaryRow[];
  testId: string;
}

const QUIET_LINE = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';

export function ChargeSummary({
  mark = null,
  amount,
  amountQualifier = null,
  recipient = null,
  recipientAction = null,
  cardTop = null,
  caption = null,
  context = null,
  items = [],
  itemsOverflow = null,
  rows,
  testId,
}: ChargeSummaryProps) {
  const contextLines = context === null ? [] : typeof context === 'string' ? [context] : context;
  const hasTop = cardTop !== null && cardTop !== undefined && cardTop !== false;
  const hasCard = hasTop || items.length > 0 || rows.length > 0;
  return (
    <section aria-label="Donation summary" className="flex flex-col gap-16" {...testIdProp(testId)}>
      <div className="flex flex-col items-center gap-10 text-center">
        {mark !== null &&
          (mark.locked === true ? (
            <LockMark size="lg" testId={`${testId}-mark`} />
          ) : (
            <Avatar
              src={mark.logoUrl}
              name={mark.name}
              size="lg"
              className="border border-border-subtle"
              testId={`${testId}-mark`}
            />
          ))}
        <div className="flex flex-col items-center gap-4">
          {amountQualifier !== null && (
            <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary">
              {amountQualifier}
            </p>
          )}
          <p
            className="font-sans text-s32 font-w700 leading-h12 tracking-givmo text-text-strong tabular-nums"
            {...testIdProp(`${testId}-amount`)}
          >
            {amount}
          </p>
          {recipient !== null && (
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {typeof recipient === 'string' ? (
                <>
                  to <span className="font-w600 text-text-strong">{recipient}</span>
                </>
              ) : (
                <>
                  to {recipient.lead}
                  <span className="font-w600 text-text-strong">{recipient.name}</span>
                  {recipient.trail}
                </>
              )}
            </p>
          )}
          {recipientAction}
          {recipient === null && caption !== null && (
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {caption}
            </p>
          )}
          {contextLines.map((line) => (
            <p key={line} className={QUIET_LINE}>
              {line}
            </p>
          ))}
        </div>
      </div>
      {hasCard && (
        <div className="flex flex-col rounded-lg border border-border-subtle">
          {hasTop && <div className="px-12 py-10">{cardTop}</div>}
          {items.length > 0 && (
            <ul
              aria-label="Matches"
              className={`flex flex-col ${hasTop ? 'border-t border-border-subtle' : ''}`}
              {...testIdProp(`${testId}-items`)}
            >
              {items.map((item, index) => (
                <li
                  key={item.key}
                  className={`flex items-center gap-10 px-12 py-8 ${
                    index > 0 ? 'border-t border-border-subtle' : ''
                  }`}
                  {...(item.testId !== undefined ? testIdProp(item.testId) : {})}
                >
                  {item.mark.locked === true ? (
                    <LockMark size="sm" />
                  ) : (
                    <Avatar
                      src={item.mark.logoUrl}
                      name={item.mark.name}
                      size="sm"
                      className="border border-border-subtle"
                    />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="truncate font-sans text-s13 font-w600 leading-h15 tracking-givmo text-text-strong">
                      {item.title}
                    </p>
                    {item.subtitle !== null && (
                      <p className="truncate font-sans text-s12 font-w400 leading-h15 tracking-normal text-text-tertiary">
                        {item.subtitle}
                      </p>
                    )}
                  </div>
                  <p className="shrink-0 font-sans text-s13 font-w600 leading-h15 tracking-givmo text-text-strong tabular-nums">
                    {item.amount}
                  </p>
                </li>
              ))}
              {itemsOverflow !== null && (
                <li className="border-t border-border-subtle px-12 py-8">
                  <p className={QUIET_LINE}>{itemsOverflow}</p>
                </li>
              )}
            </ul>
          )}
          {rows.length > 0 && (
            <dl
              className={`flex flex-col ${
                hasTop || items.length > 0 ? 'border-t border-border-subtle' : ''
              }`}
            >
              {rows.map((row, index) => (
                <div
                  key={row.label}
                  className={`flex items-baseline justify-between gap-12 px-12 py-10 ${
                    index > 0 ? 'border-t border-border-subtle' : ''
                  }`}
                  {...(row.testId !== undefined ? testIdProp(row.testId) : {})}
                >
                  <dt className="shrink-0 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                    {row.label}
                  </dt>
                  <dd className="min-w-0 text-right font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-strong tabular-nums">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </section>
  );
}
